import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Calendar, Loader2, Building, HelpCircle, Key, Eye, EyeOff } from "lucide-react";
import { isIOSApp } from "@/utils/platform";

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  // Nell'app iOS è consentita SOLO la registrazione del dipendente con codice invito
  // (nessun account aziendale, nessun piano/prezzo — Apple Guideline 3.1.1 / 3.1.3(b)).
  const isIOSAppEnv = isIOSApp();
  const forcedRole = isIOSAppEnv ? null : searchParams.get("role");
  const wantsProLight = !isIOSAppEnv && forcedRole === "titolare" && searchParams.get("plan") === "pro_light";

  const [formData, setFormData] = useState({
    cognome: "",
    nome: "",
    email: "",
    password: "",
    ruolo: forcedRole || "dipendente",
    posizione: "",
    nome_azienda: "",
    codice_invito: ""
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [checkingSystem, setCheckingSystem] = useState(true);
  const [isFirstSetup, setIsFirstSetup] = useState(false);
  // Invite code lookup (used to hide "amministrativo" on Base plan companies)
  const [inviteInfo, setInviteInfo] = useState(null); // { valid, nome_azienda, piano, allow_amministrativo }
  const { register, settings, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isIOSAppEnv) {
      // Su iOS il sistema è già configurato: nessuna chiamata a system/status, si registra solo come dipendente.
      setIsFirstSetup(false);
      setCheckingSystem(false);
      return;
    }
    checkSystemStatus();
  }, []);

  // When the user types a 6-character invite code, lookup the company plan
  useEffect(() => {
    const code = (formData.codice_invito || "").trim().toUpperCase();
    if (formData.ruolo === "titolare" || code.length < 6) {
      setInviteInfo(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/check-invite-code/${code}`);
        if (cancelled) return;
        setInviteInfo(res.data);
        // If the code points to a Base plan company and user selected "amministrativo", reset to dipendente
        if (res.data?.allow_amministrativo === false && formData.ruolo === "amministrativo") {
          setFormData((prev) => ({ ...prev, ruolo: "dipendente" }));
        }
      } catch (e) {
        if (!cancelled) setInviteInfo({ valid: false });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.codice_invito, formData.ruolo]);

  const checkSystemStatus = async () => {
    try {
      const response = await axios.get(`${API}/system/status`);
      setIsFirstSetup(response.data.is_first_setup);
      // If first setup, force role to titolare
      if (response.data.is_first_setup) {
        setFormData(prev => ({ ...prev, ruolo: "titolare" }));
      }
    } catch (error) {
      console.error("Failed to check system status");
    } finally {
      setCheckingSystem(false);
    }
  };

  const detectRegistrationSource = () => {
    const cap = window.Capacitor;
    if (cap?.getPlatform) {
      const p = cap.getPlatform();
      if (p === "android") return "android_app";
      if (p === "ios") return "ios_app";
    }
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return "pwa";
    return "web";
  };

  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!termsAccepted) {
      toast.error("Per continuare devi accettare i Termini di servizio e la Privacy Policy");
      return;
    }
    setLoading(true);
    try {
      await register({ ...formData, registration_source: detectRegistrationSource(), terms_accepted: true });
      
      if (formData.ruolo === "titolare") {
        toast.success("Account aziendale creato!");
        try {
          await login(formData.email, formData.password);
          // Small delay to let React state settle before navigation
          await new Promise(resolve => setTimeout(resolve, 300));
          if (wantsProLight) {
            // Acquisto Pro Light dal sito: apri subito il pagamento Stripe
            try {
              const res = await axios.post(
                `${API}/v2/checkout`,
                { origin_url: window.location.origin },
                { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
              );
              toast.success("Account creato! Ti portiamo al pagamento di Pro Light…");
              window.location.href = res.data.url;
              return;
            } catch (checkoutError) {
              console.error("Pro Light checkout failed after registration:", checkoutError);
              toast.info("Account creato. Puoi attivare Pro Light dalla pagina Abbonamento.");
              navigate("/abbonamento");
              return;
            }
          }
          // Always land on Home after registration. Wizard stays available in /settings.
          navigate("/dashboard");
        } catch (loginError) {
          // Auto-login failed - redirect to login page so user can login manually
          console.error("Auto-login failed after registration:", loginError);
          toast.success("Account creato! Effettua il login per continuare.");
          navigate("/login");
        }
      } else {
        toast.success("Registrazione completata! Ora puoi accedere.");
        navigate("/login");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante la registrazione");
    } finally {
      setLoading(false);
    }
  };

  if (checkingSystem) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  // Determine available roles
  const isTitolareRegistration = isFirstSetup || forcedRole === "titolare";
  
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left side - Form */}
      <div className="flex items-center justify-center p-4 sm:p-8 lg:p-12 pb-20 sm:pb-8 bg-white">
        <div className="w-full max-w-md space-y-6 sm:space-y-8 animate-fade-in">
          {/* Logo */}
          <div className="flex flex-col items-center text-center gap-4">
            <img 
              src="/logo_ferie_permessi.jpg" 
              alt="Ferie e Permessi" 
              className="h-24 w-auto object-contain rounded-lg shadow-md"
            />
            <div>
              <h2 className="text-xl font-bold text-slate-900">Ferie e Permessi</h2>
              <p className="text-sm text-slate-500">
                {isFirstSetup ? "Configura la tua azienda" : "Gestione Aziendale Ferie e Permessi"}
              </p>
              <p className="text-xs text-slate-400 mt-1">by LuiCas ™</p>
            </div>
          </div>

          <div className="space-y-2 text-center">
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">
              {isTitolareRegistration ? "Crea account aziendale" : "Registrati"}
            </h1>
            <p className="text-slate-600">
              {isTitolareRegistration 
                ? "Compila i campi per registrare la tua azienda" 
                : "Compila i campi per registrarti"}
            </p>
          </div>

          {/* Acquisto Pro Light dal sito */}
          {wantsProLight && (
            <Card className="border-amber-300 bg-amber-50" data-testid="register-pro-light-banner">
              <CardContent className="py-4">
                <p className="text-sm text-amber-900">
                  <strong>Stai acquistando Pro Light — €4,90/mese.</strong> Passo 1 di 2: crea l'account aziendale.
                  Subito dopo passerai al pagamento sicuro con carta (Stripe). Puoi disdire quando vuoi.
                </p>
              </CardContent>
            </Card>
          )}

          {/* First setup instructions */}
          {isFirstSetup && (
            <Card className="card-theme-highlight">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-theme-100 rounded-lg shrink-0">
                    <HelpCircle className="h-5 w-5 text-theme-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 text-sm">Primo passo</h3>
                    <p className="text-sm text-slate-600 mt-1">
                      Stai creando l'account <strong>Titolare</strong>. Dopo la registrazione potrai configurare le impostazioni aziendali.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cognome" className="text-slate-700">Cognome</Label>
                <Input
                  id="cognome"
                  placeholder="Rossi"
                  value={formData.cognome}
                  onChange={(e) => setFormData({ ...formData, cognome: e.target.value })}
                  required
                  data-testid="register-cognome-input"
                  className="h-12 border-slate-200 focus:ring-2 focus:ring-slate-900"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="nome" className="text-slate-700">Nome</Label>
                <Input
                  id="nome"
                  placeholder="Mario"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  required
                  data-testid="register-nome-input"
                  className="h-12 border-slate-200 focus:ring-2 focus:ring-slate-900"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="mario.rossi@azienda.it"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="register-email-input"
                className="h-12 border-slate-200 focus:ring-2 focus:ring-slate-900"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={6}
                  data-testid="register-password-input"
                  className="h-12 pr-12 border-slate-200 focus:ring-2 focus:ring-slate-900"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="register-password-toggle"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="posizione" className="text-slate-700">Posizione lavorativa</Label>
              <Input
                id="posizione"
                placeholder={forcedRole === "titolare" ? "Es: Titolare, CEO, AD, Responsabile HR" : "Es: Segretaria, Commercialista, Responsabile HR"}
                value={formData.posizione}
                onChange={(e) => setFormData({ ...formData, posizione: e.target.value })}
                data-testid="register-posizione-input"
                className="h-12 border-slate-200 focus:ring-2 focus:ring-slate-900"
              />
              <p className="text-xs text-slate-500">
                Opzionale - La tua mansione in azienda
              </p>
            </div>

            {/* Role selection - hidden for first setup, shown for others */}
            {isTitolareRegistration ? (
              <>
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-center gap-3">
                    <Building className="h-5 w-5 text-slate-600" />
                    <div>
                      <p className="font-medium text-slate-900">Ruolo: Titolare</p>
                      <p className="text-sm text-slate-500">Avrai accesso completo a tutte le funzionalità</p>
                    </div>
                  </div>
                </div>
                
                {/* Company name for Titolare */}
                <div className="space-y-2">
                  <Label htmlFor="nome_azienda" className="text-slate-700">Nome Azienda</Label>
                  <Input
                    id="nome_azienda"
                    placeholder="Es: Studio Rossi S.r.l."
                    value={formData.nome_azienda}
                    onChange={(e) => setFormData({ ...formData, nome_azienda: e.target.value })}
                    required
                    data-testid="register-nome-azienda-input"
                    className="h-12 border-slate-200 focus:ring-2 focus:ring-slate-900"
                  />
                  <p className="text-xs text-slate-500">
                    Questo sarà il nome visualizzato nell'app
                  </p>
                </div>
              </>
            ) : (
              <>
                <input type="hidden" value="dipendente" data-testid="register-ruolo-hidden" />
                <div key="field-codice-invito" className="space-y-2">
                  <Label htmlFor="codice_invito" className="text-slate-700 flex items-center gap-2">
                    <Key className="h-4 w-4" />
                    Codice Invito
                  </Label>
                  <Input
                    id="codice_invito"
                    placeholder="Es: ABC123"
                    value={formData.codice_invito}
                    onChange={(e) => setFormData({ ...formData, codice_invito: e.target.value.toUpperCase() })}
                    required
                    maxLength={6}
                    data-testid="register-codice-invito-input"
                    className="h-12 border-slate-200 focus:ring-2 focus:ring-slate-900 uppercase tracking-widest text-center font-mono text-lg"
                  />
                  <p className="text-xs text-slate-500">
                    Richiedi il codice al titolare della tua azienda. Verrai registrato come Dipendente.
                  </p>
                  {inviteInfo?.valid && (
                    <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-2">
                      Azienda: <strong>{inviteInfo.nome_azienda}</strong>
                    </p>
                  )}
                </div>
                {!isIOSAppEnv && (
                <p className="text-xs text-slate-500 text-center pt-2">
                  Sei un titolare?{" "}
                  <button
                    type="button"
                    onClick={() => navigate("/register?role=titolare")}
                    className="text-slate-900 font-medium hover:underline"
                    data-testid="switch-to-titolare-link"
                  >
                    Crea un nuovo account aziendale
                  </button>
                </p>
                )}
              </>
            )}

            <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer select-none" data-testid="terms-accept-label">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300"
                data-testid="terms-accept-checkbox"
              />
              <span>
                Ho letto e accetto i{" "}
                <a href="/terms.html" target="_blank" rel="noopener noreferrer" className="underline text-slate-800" data-testid="terms-link">Termini di servizio</a>
                {" "}(inclusa la limitazione di responsabilità) e la{" "}
                <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer" className="underline text-slate-800" data-testid="privacy-link">Privacy Policy</a>.
              </span>
            </label>

            <Button
              type="submit"
              disabled={loading || !termsAccepted}
              data-testid="register-submit-btn"
              className="w-full h-12 btn-theme rounded-md btn-press font-medium"
            >
              {loading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Registrazione...</>
              ) : (
                isTitolareRegistration ? (wantsProLight ? "Crea account e vai al pagamento" : "Crea Account Aziendale") : "Registrati"
              )}
            </Button>
          </form>

          <p className="text-center text-slate-600">
            {isFirstSetup ? (
              <span className="text-sm">Hai bisogno di aiuto? Contatta il supporto</span>
            ) : (
              <>
                Hai già un account?{" "}
                <Link to="/login" className="text-slate-900 font-medium hover:underline" data-testid="login-link">
                  Accedi
                </Link>
              </>
            )}
          </p>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block relative">
        <img
          src="https://customer-assets.emergentagent.com/job_aea9fcf4-7192-4048-99b8-cf6b21f11caa/artifacts/1flrp8z8_Team.png"
          alt="Team working together"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/20"></div>
      </div>
    </div>
  );
}
